import React, { useState } from "react";
import { Stack, Dialog, DialogContent, DialogHeader, DialogTitle } from "@altimateai/lego";
import { TrialResult } from "../../services/adeBench";
import { StatusBadge } from "./StatusBadge";
import { FailureModeBadge } from "./FailureModeBadge";

interface TrialTableProps {
  results: TrialResult[];
}

const formatDuration = (ms: number | null | undefined): string => {
  if (ms === null || ms === undefined) return "-";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

const formatTokens = (count: number | null | undefined): string => {
  if (count === null || count === undefined) return "-";
  if (count < 1000) return count.toString();
  return `${(count / 1000).toFixed(1)}k`;
};

export const TrialTable: React.FC<TrialTableProps> = ({ results }) => {
  const [selectedTrial, setSelectedTrial] = useState<TrialResult | null>(null);

  if (!results || results.length === 0) {
    return <div className="al-text-gray-500 al-text-sm">No trial results found</div>;
  }

  // Sort by task_id
  const sortedResults = [...results].sort((a, b) => a.task_id.localeCompare(b.task_id));

  return (
    <>
      <div className="al-overflow-x-auto">
        <table className="al-w-full al-text-sm">
          <thead className="al-bg-gray-100">
            <tr>
              <th className="al-p-2 al-text-left al-font-medium">Task</th>
              <th className="al-p-2 al-text-left al-font-medium">Trial</th>
              <th className="al-p-2 al-text-left al-font-medium">Status</th>
              <th className="al-p-2 al-text-left al-font-medium">Failure</th>
              <th className="al-p-2 al-text-right al-font-medium">Duration</th>
              <th className="al-p-2 al-text-right al-font-medium">Tokens</th>
              <th className="al-p-2 al-text-right al-font-medium">Cost</th>
              <th className="al-p-2 al-text-center al-font-medium">Trace</th>
            </tr>
          </thead>
          <tbody>
            {sortedResults.map((trial, idx) => (
              <tr
                key={`${trial.task_id}-${trial.trial_name}-${idx}`}
                className="al-border-b al-border-gray-200 hover:al-bg-gray-100 al-cursor-pointer"
                onClick={() => setSelectedTrial(trial)}
              >
                <td className="al-p-2 al-font-mono al-text-xs">{trial.task_id}</td>
                <td className="al-p-2 al-text-gray-600">{trial.trial_name}</td>
                <td className="al-p-2">
                  <StatusBadge isResolved={trial.is_resolved} />
                </td>
                <td className="al-p-2">
                  <FailureModeBadge mode={trial.failure_mode} />
                </td>
                <td className="al-p-2 al-text-right al-text-gray-600">
                  {formatDuration(trial.runtime_ms)}
                </td>
                <td className="al-p-2 al-text-right al-text-gray-600">
                  <Stack direction="row" gap="xs" justify="end">
                    <span title="Input tokens">{formatTokens(trial.input_tokens)}</span>
                    <span className="al-text-gray-400">/</span>
                    <span title="Output tokens">{formatTokens(trial.output_tokens)}</span>
                  </Stack>
                </td>
                <td className="al-p-2 al-text-right al-text-gray-600">
                  {trial.cost_usd ? `$${trial.cost_usd.toFixed(4)}` : "-"}
                </td>
                <td className="al-p-2 al-text-center">
                  {trial.langfuse_trace_url ? (
                    <a
                      href={trial.langfuse_trace_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="al-text-blue-600 hover:al-underline"
                      onClick={e => e.stopPropagation()}
                    >
                      View
                    </a>
                  ) : (
                    <span className="al-text-gray-400">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selectedTrial} onOpenChange={() => setSelectedTrial(null)}>
        <DialogContent className="al-max-w-2xl al-max-h-[80vh] al-overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Trial: {selectedTrial?.task_id} - {selectedTrial?.trial_name}
            </DialogTitle>
          </DialogHeader>
          {selectedTrial && (
            <div className="al-space-y-4">
              <div className="al-grid al-grid-cols-2 al-gap-4 al-text-sm">
                <div>
                  <span className="al-font-medium al-text-gray-500">Status:</span>
                  <div className="al-mt-1">
                    <StatusBadge isResolved={selectedTrial.is_resolved} />
                  </div>
                </div>
                <div>
                  <span className="al-font-medium al-text-gray-500">Failure Mode:</span>
                  <div className="al-mt-1">
                    <FailureModeBadge mode={selectedTrial.failure_mode} />
                  </div>
                </div>
                <div>
                  <span className="al-font-medium al-text-gray-500">Runtime:</span>
                  <div className="al-mt-1">{formatDuration(selectedTrial.runtime_ms)}</div>
                </div>
                <div>
                  <span className="al-font-medium al-text-gray-500">Cost:</span>
                  <div className="al-mt-1">
                    {selectedTrial.cost_usd ? `$${selectedTrial.cost_usd.toFixed(4)}` : "-"}
                  </div>
                </div>
                <div>
                  <span className="al-font-medium al-text-gray-500">Tokens (In/Out):</span>
                  <div className="al-mt-1">
                    {selectedTrial.input_tokens?.toLocaleString() ?? "-"} /{" "}
                    {selectedTrial.output_tokens?.toLocaleString() ?? "-"}
                  </div>
                </div>
                <div>
                  <span className="al-font-medium al-text-gray-500">Turns:</span>
                  <div className="al-mt-1">{selectedTrial.num_turns ?? "-"}</div>
                </div>
                {selectedTrial.agent && (
                  <div>
                    <span className="al-font-medium al-text-gray-500">Agent:</span>
                    <div className="al-mt-1">{selectedTrial.agent}</div>
                  </div>
                )}
                {selectedTrial.model_name && (
                  <div>
                    <span className="al-font-medium al-text-gray-500">Model:</span>
                    <div className="al-mt-1">{selectedTrial.model_name}</div>
                  </div>
                )}
                {selectedTrial.langfuse_trace_url && (
                  <div className="al-col-span-2">
                    <span className="al-font-medium al-text-gray-500">Langfuse Trace:</span>
                    <div className="al-mt-1">
                      <a
                        href={selectedTrial.langfuse_trace_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="al-text-blue-600 hover:al-underline"
                      >
                        View in Langfuse →
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {selectedTrial.task_prompt && (
                <div>
                  <span className="al-font-medium al-text-gray-500 al-text-sm">Task Prompt:</span>
                  <div className="al-mt-1 al-p-3 al-bg-gray-50 al-rounded al-text-sm al-font-mono al-whitespace-pre-wrap al-max-h-40 al-overflow-y-auto">
                    {selectedTrial.task_prompt}
                  </div>
                </div>
              )}

              {selectedTrial.parser_results &&
                Object.keys(selectedTrial.parser_results).length > 0 && (
                  <div>
                    <span className="al-font-medium al-text-gray-500 al-text-sm">
                      Parser Results:
                    </span>
                    <div className="al-mt-1 al-p-3 al-bg-gray-50 al-rounded al-text-sm al-font-mono al-whitespace-pre-wrap al-max-h-40 al-overflow-y-auto">
                      {JSON.stringify(selectedTrial.parser_results, null, 2)}
                    </div>
                  </div>
                )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
